import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface ContentBlock {
  subtitle: string;
  title: string;
  text: string;
  imageUrl: string;
  buttonText: string;
  buttonLink: string;
}

export interface ContentBlocks {
  textImage: ContentBlock; // text left, image right
  imageText: ContentBlock; // image left, text right
  updatedAt?: Date;
}

export const emptyBlock: ContentBlock = {
  subtitle: '',
  title: '',
  text: '',
  imageUrl: '',
  buttonText: '',
  buttonLink: '',
};

const defaultBlocks: ContentBlocks = {
  textImage: {
    ...emptyBlock,
    subtitle: 'За комплекса',
    title: 'Модерна архитектура край морето',
    text: 'Кратко описание на предимствата на сградата. Редактирайте този текст и добавете снимка от админ панела → „Текстови секции".',
  },
  imageText: {
    ...emptyBlock,
    subtitle: 'Качество',
    title: 'Луксозни довършителни работи',
    text: 'Кратко описание за материалите и обзавеждането. Редактирайте този текст и добавете снимка от админ панела → „Текстови секции".',
  },
};

const withDefaults = (data: any): ContentBlocks => ({
  textImage: { ...defaultBlocks.textImage, ...(data?.textImage || {}) },
  imageText: { ...defaultBlocks.imageText, ...(data?.imageText || {}) },
});

export const useContentBlocks = () => {
  const [blocks, setBlocks] = useState<ContentBlocks>(defaultBlocks);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBlocks = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, 'content', 'contentBlocks'));
      if (snap.exists()) {
        const data = snap.data();
        setBlocks({ ...withDefaults(data), updatedAt: data.updatedAt?.toDate() });
      } else {
        setBlocks(defaultBlocks);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching content blocks:', err);
      setError('Грешка при зареждане на секциите');
      setBlocks(defaultBlocks);
    } finally {
      setLoading(false);
    }
  };

  const saveBlocks = async (next: { textImage: ContentBlock; imageText: ContentBlock }) => {
    try {
      await setDoc(doc(db, 'content', 'contentBlocks'), {
        textImage: next.textImage,
        imageText: next.imageText,
        updatedAt: serverTimestamp(),
      });
      setBlocks({ ...next, updatedAt: new Date() });
      return true;
    } catch (err) {
      console.error('Error saving content blocks:', err);
      setError('Грешка при запазване на секциите');
      return false;
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  return { blocks, loading, error, saveBlocks, refreshBlocks: fetchBlocks };
};
